import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../services/api';
import chunkedUploadService from '../services/chunkedUploadService';
import './AppUpload.css';

const AppUpload = ({ onSuccess, onClose, parentAppId = null }) => {
    const { currentWorkspace } = useWorkspace();
    const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'url'
    const [platform, setPlatform] = useState('android');
    const [displayName, setDisplayName] = useState('');
    const [file, setFile] = useState(null);
    const [url, setUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadDetails, setUploadDetails] = useState({
        uploadedChunks: 0,
        totalChunks: 0,
        speed: 0,
        eta: 0
    });
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null)
    const [dragActive, setDragActive] = useState(false);
    const [abortController, setAbortController] = useState(null);
    const fileInputRef = useRef(null);

    // File size limit (must match backend)
    const MAX_FILE_SIZE = 1000 * 1024 * 1024; // 1000MB

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (selectedFile) => {
        const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

        // Auto-detect platform from file extension
        let detectedPlatform = null;
        if (fileExt === '.apk') {
            detectedPlatform = 'android';
        } else if (fileExt === '.ipa') {
            detectedPlatform = 'ios';
        } else {
            setError('Invalid file type. Please upload an APK (Android) or IPA (iOS) file.');
            return;
        }

        const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;
        if (detectedPlatform === 'ios' && !realDevicesEnabled) {
            setError(
                <span>
                    iOS app testing requires a premium plan.{' '}
                    <Link to="/settings/billing" style={{ textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link>{' '}
                    to upload and test iOS apps.
                </span>
            );
            setErrorType('premium_feature_required');
            return;
        }

        // Validate file size
        if (selectedFile.size > MAX_FILE_SIZE) {
            setError(`File size (${(selectedFile.size / (1024 * 1024)).toFixed(2)}MB) exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
            return;
        }

        if (selectedFile.size === 0) {
            setError('File is empty. Please select a valid app file.');
            return;
        }

        setPlatform(detectedPlatform);
        setFile(selectedFile);
        setError(null);
        if (!displayName) {
            setDisplayName(selectedFile.name);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    };

    const handleFileUpload = async () => {
        if (!file) {
            setError('Please select a file');
            return;
        }

        if (!currentWorkspace?.id) {
            setError('No workspace selected');
            return;
        }

        setUploading(true);
        setError(null);
        setUploadProgress(0);
        setUploadDetails({ uploadedChunks: 0, totalChunks: 0, speed: 0, eta: 0 });

        // Create abort controller
        const controller = new AbortController();
        setAbortController(controller);

        try {
            const result = await chunkedUploadService.uploadFile(file, platform, {
                parentAppId,
                onProgress: (progress) => {
                    setUploadProgress(progress.percentage);
                    setUploadDetails({
                        uploadedChunks: progress.uploadedChunks,
                        totalChunks: progress.totalChunks,
                        speed: progress.speed,
                        eta: progress.eta
                    });
                },
                onChunkComplete: (chunkIndex, progress) => {
                    console.log(`Chunk ${chunkIndex} uploaded:`, progress);
                },
                onError: (err) => {
                    console.error('Upload error:', err);
                },
                signal: controller.signal
            });

            console.log('Upload complete:', result);

            if (onSuccess) {
                onSuccess(result.app || result.build);
            }
        } catch (err) {
            console.error('Upload failed:', err);

            if (err.message === 'Upload aborted') {
                setError('Upload cancelled');
            } else {
                setError(err.response?.data?.error || err.message || 'Upload failed');
                if (err.response?.data?.errorType) {
                    setErrorType(err.response.data.errorType);
                }
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setAbortController(null);
        }
    };

    const handleCancelUpload = () => {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
        }
    };

    const handleUrlUpload = async () => {
        if (!url) {
            setError('Please enter a URL');
            return;
        }

        const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;
        if (platform === 'ios' && !realDevicesEnabled) {
            setError(
                <span>
                    iOS app testing requires a premium plan.{' '}
                    <Link to="/settings/billing" style={{ textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link>{' '}
                    to upload and test iOS apps.
                </span>
            );
            setErrorType('premium_feature_required');
            return;
        }

        try {
            setUploading(true);
            setError(null);

            const response = await api.post('/apps/upload-from-url', {
                url,
                platform,
                displayName: displayName || url.split('/').pop(),
            });

            onSuccess(response.data.app);
        } catch (err) {
            setError(err.response?.data?.error || 'Upload failed');
            console.error('URL upload error:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (uploadMethod === 'file') {
            handleFileUpload();
        } else {
            handleUrlUpload();
        }
    };

    return (
        <div className="upload-modal-overlay" onClick={onClose}>
            <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
                <div className="upload-modal-header">
                    <h2>{parentAppId ? 'Upload Build' : 'Upload App'}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit} className="upload-form">
                    {/* Upload Method Tabs */}
                    <div className="upload-tabs">
                        <button
                            type="button"
                            className={`tab ${uploadMethod === 'file' ? 'active' : ''}`}
                            onClick={() => setUploadMethod('file')}
                        >
                            📁 File Upload
                        </button>
                        <button
                            type="button"
                            className={`tab ${uploadMethod === 'url' ? 'active' : ''}`}
                            onClick={() => setUploadMethod('url')}
                        >
                            🔗 URL Upload
                        </button>
                    </div>

                    {/* Platform Selection - Only for URL uploads */}
                    {uploadMethod === 'url' && (
                        <div className="form-group">
                            <label>Platform *</label>
                            <div className="platform-selector">
                                <button
                                    type="button"
                                    className={`platform-btn ${platform === 'android' ? 'active' : ''}`}
                                    onClick={() => {
                                        setPlatform('android');
                                        setError(null);
                                        setErrorType(null);
                                    }}
                                >
                                    🤖 Android
                                </button>
                                <button
                                    type="button"
                                    className={`platform-btn ${platform === 'ios' ? 'active' : ''}`}
                                    onClick={() => {
                                        const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;
                                        if (!realDevicesEnabled) {
                                            setError(
                                                <span>
                                                    iOS app testing requires a premium plan.{' '}
                                                    <Link to="/settings/billing" style={{ textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link>{' '}
                                                    to upload and test iOS apps.
                                                </span>
                                            );
                                            setErrorType('premium_feature_required');
                                            return;
                                        }
                                        setPlatform('ios');
                                    }}
                                >
                                    🍎 iOS
                                </button>
                            </div>
                        </div>
                    )}

                    {/* File Upload */}
                    {uploadMethod === 'file' && (
                        <div className="form-group">
                            <label>App File *</label>
                            <div
                                className={`dropzone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".apk,.ipa"
                                    onChange={handleFileInputChange}
                                    style={{ display: 'none' }}
                                />
                                {file ? (
                                    <div className="file-info">
                                        <div className="file-icon">📦</div>
                                        <div className="file-details">
                                            <div className="file-name">{file.name}</div>
                                            <div className="file-size">
                                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="remove-file-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFile(null);
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <div className="dropzone-content">
                                        <div className="upload-icon">☁️</div>
                                        <p className="dropzone-text">
                                            Drag & drop your APK or IPA file here
                                        </p>
                                        <p className="dropzone-subtext">or click to browse</p>
                                    </div>
                                )}
                            </div>
                            <p className="input-hint">
                                Maximum file size: {MAX_FILE_SIZE / (1024 * 1024)}MB
                            </p>
                        </div>
                    )}

                    {/* URL Upload */}
                    {uploadMethod === 'url' && (
                        <div className="form-group">
                            <label>App URL *</label>
                            <input
                                type="url"
                                className="form-input"
                                placeholder="https://example.com/app.apk"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                required
                            />
                            <p className="input-hint">
                                Enter a direct public download link to your app (e.g., from GitHub releases, CI/CD artifacts)
                            </p>
                        </div>
                    )}

                    {/* Display Name */}
                    {/* <div className="form-group">
                        <label>Display Name (Optional)</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="My Awesome App"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                    </div> */}

                    {/* Upload Progress */}
                    {uploading && uploadMethod === 'file' && (
                        <div className="upload-progress">
                            <div className="appupload__progress-bar">
                                <div
                                    className="appupload__progress-fill"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                            <div className="appupload__progress-details">
                                <div className="appupload__progress-text">{uploadProgress}%</div>
                                {uploadDetails.totalChunks > 0 && (
                                    <div className="appupload__progress-info">
                                        {/* <span className="appupload__progress-chunks">
                                            {uploadDetails.uploadedChunks}/{uploadDetails.totalChunks} chunks
                                        </span> */}
                                        {uploadDetails.speed > 0 && (
                                            <>
                                                <span className="appupload__progress-separator">•</span>
                                                <span className="appupload__progress-speed">
                                                    {chunkedUploadService.formatBytes(uploadDetails.speed)}/s
                                                </span>
                                            </>
                                        )}
                                        {uploadDetails.eta > 0 && (
                                            <>
                                                <span className="appupload__progress-separator">•</span>
                                                <span className="appupload__progress-eta">
                                                    {chunkedUploadService.formatTime(uploadDetails.eta)} remaining
                                                </span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            {abortController && (
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCancelUpload}
                                    style={{ marginTop: '10px' }}
                                >
                                    Cancel Upload
                                </button>
                            )}
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="error-message">
                            {error}
                            {errorType && errorType === 'quota_limit_reached' && (
                                <Link to="/settings/billing" className="btn btn-ghost">Upgrade Plan</Link>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="upload-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={uploading || (uploadMethod === 'file' && !file) || (uploadMethod === 'url' && !url)}
                        >
                            {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AppUpload;
